/**
 * MCP Skill 详情页面
 *
 * 显示单个技能的完整信息，包括：
 * - 基本信息（名称、描述、评分等）
 * - 使用场景
 * - 快速开始指南
 * - 安装命令和配置示例
 * - 官方文档和仓库链接
 * - 相关推荐技能
 */

import React, { useState } from 'react';
import { ArrowLeft, Star, Download, Copy, Check, Package, Tag, BookOpen, Code, Terminal, Settings, Github, Globe } from 'lucide-react';
import { Skill, getInstallCommand } from '../api/skillApi';

interface SkillDetailPageProps {
  skill: Skill;
  lang?: 'zh' | 'en';
  onBack: () => void;
  onSkillClick?: (skillName: string) => void;
  relatedSkills?: Skill[];
}

export default function SkillDetailPage({ skill, lang = 'zh', onBack, onSkillClick, relatedSkills = [] }: SkillDetailPageProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyCommand = () => {
    const command = getInstallCommand(skill);
    navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const installCommand = getInstallCommand(skill);

  return (
    <div className="min-h-screen bg-[#F5F7FA] dark:bg-[#1E1E2E]">
      {/* 顶部导航 */}
      <header className="bg-white dark:bg-[#2D2D3F] shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-16">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {lang === 'zh' ? '返回首页' : 'Back to Home'}
            </button>
          </div>
        </div>
      </header>

      {/* 主内容 */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* 标题区域 */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">
                  {skill.name}
                </h1>
                <div className="flex items-center gap-2">
                  <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                  <span className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                    {skill.rating}
                  </span>
                </div>
              </div>

              <div className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                by <span className="font-semibold text-purple-600 dark:text-purple-400">{skill.publisher}</span>
              </div>

              <p className="text-xl text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                {skill.description}
              </p>

              {/* 分类和标签 */}
              <div className="flex flex-wrap gap-2 mb-6">
                {skill.categories.map((category, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full text-sm font-medium whitespace-nowrap"
                  >
                    <Tag className="w-3.5 h-3.5" />
                    {category}
                  </span>
                ))}
                {skill.tags && skill.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full text-sm whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* 右上角：统计信息 + 官方链接 */}
            <div className="flex flex-col items-end gap-2">
              {/* 统计信息 */}
              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1.5">
                  <Download className="w-4 h-4" />
                  <span>{skill.downloads}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Package className="w-4 h-4" />
                  <span>{skill.license}</span>
                </div>
              </div>

              {/* 官方链接 */}
              <div className="flex gap-2">
                <a
                  href={skill.homepage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 transition-colors"
                  title={lang === 'zh' ? '查看主页' : 'View Homepage'}
                >
                  <Globe className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </a>
                <a
                  href={skill.repository}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 transition-colors"
                  title={lang === 'zh' ? '查看仓库' : 'View Repository'}
                >
                  <Github className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* 安装命令 */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Terminal className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              {lang === 'zh' ? '安装命令' : 'Installation'}
            </h2>
          </div>
          <div className="bg-gray-900 dark:bg-gray-950 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <code className="text-sm text-green-400 font-mono">
                {installCommand}
              </code>
              <button
                onClick={handleCopyCommand}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    {lang === 'zh' ? '已复制' : 'Copied!'}
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    {lang === 'zh' ? '复制命令' : 'Copy Command'}
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-400">
              {lang === 'zh' ? '在终端中运行此命令来安装该 MCP Server' : 'Run this command in your terminal to install the MCP Server'}
            </p>
          </div>
        </div>

        {/* 使用场景 & 快速开始 - 两列布局 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 mb-8 items-stretch">
          {/* 使用场景 */}
          {skill.useCases && skill.useCases.length > 0 && (
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                  {lang === 'zh' ? '使用场景：' : 'Use Cases:'}
                </h2>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 flex-1">
                <ul className="space-y-3">
                  {skill.useCases.map((useCase, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                      <span className="text-purple-500 mt-1 flex-shrink-0">•</span>
                      <span>{useCase}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* 快速开始 */}
          {skill.quickStart && (
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <Code className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                  {lang === 'zh' ? '快速开始：' : 'Quick Start:'}
                </h2>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 flex-1">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {skill.quickStart}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 相关技能 */}
        {relatedSkills && relatedSkills.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                {lang === 'zh' ? '相关 Skills' : 'Related Skills'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedSkills.map((relatedSkill) => (
                <div
                  key={relatedSkill.id}
                  onClick={() => onSkillClick && onSkillClick(relatedSkill.name)}
                  className="group relative bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-5 border border-purple-200 dark:border-purple-700 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                >
                  {/* 技能名称和评分 */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-800 dark:text-gray-200 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors mb-1">
                        {relatedSkill.name}
                      </h3>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        by <span className="text-purple-600 dark:text-purple-400">{relatedSkill.publisher}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {relatedSkill.rating}
                      </span>
                    </div>
                  </div>

                  {/* 描述 */}
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 mb-4 min-h-[60px] leading-relaxed">
                    {relatedSkill.description}
                  </p>

                  {/* 分类 */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {relatedSkill.categories.slice(0, 2).map((category, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full"
                      >
                        {category}
                      </span>
                    ))}
                  </div>

                  {/* 安装命令 */}
                  <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-2.5 mb-3">
                    <code className="text-xs text-green-400 font-mono truncate block">
                      {relatedSkill.installation}
                    </code>
                  </div>

                  {/* 底部信息 */}
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 border-t border-purple-200 dark:border-purple-800 pt-3">
                    <div className="flex items-center gap-2">
                      <span>📥 {relatedSkill.downloads}</span>
                      <span>📦 {relatedSkill.license}</span>
                    </div>
                    <div className="text-purple-600 dark:text-purple-400">
                      {lang === 'zh' ? '查看详情' : 'View Details'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 底部操作 */}
        <div className="flex justify-center">
          <a
            href={skill.repository}
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-colors font-medium"
          >
            <Github className="w-5 h-5 inline mr-2" />
            {lang === 'zh' ? '查看源代码' : 'View Source Code'}
          </a>
        </div>
      </main>
    </div>
  );
}
